import React, { useEffect, useState } from 'react';
import { collection, doc, serverTimestamp, setDoc, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { EntitySummary } from '../../hooks/useEntityContext';
import { PAGE_FEATURES } from './permissionsConfig';
import { getRulesErrorMessage } from './rolesErrors';
import { getEntityAccessRights, getSubscriptionTier, getUpgradeMessage } from '../../utils/subscriptionUtils';

type InviteRole = 'va' | 'board';

 

interface InviteRoleFormProps {
  role: InviteRole;
  activeEntity: EntitySummary | null;
  title: string;
  description: string;
  restrictToNetworkclub?: boolean;
  currentUserId: string | null | undefined;
}

const InviteRoleForm: React.FC<InviteRoleFormProps> = ({
  role,
  activeEntity,
  title,
  description,
  restrictToNetworkclub,
  currentUserId
}) => {
  const [email, setEmail] = useState('');
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const entityId = activeEntity?.id || '';

  // Load user subscription
  useEffect(() => {
    const loadSubscription = async () => {
      if (!currentUserId) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', currentUserId));
        if (userDoc.exists()) {
          setUserSubscription(userDoc.data().subscription);
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [currentUserId]);

  useEffect(() => {
    setError(null);
    setSuccess(null);
  }, [entityId]);

  const subscriptionTier = getSubscriptionTier(userSubscription);
  const accessRights = getEntityAccessRights(subscriptionTier, activeEntity?.type as any);
  const canInvite = accessRights.canInviteMembers;

  const togglePage = (pageId: string) => {
    if (selectedPages.includes(pageId)) {
      setSelectedPages(selectedPages.filter(page => page !== pageId));
      setSelectedFeatures(selectedFeatures.filter(feature => !feature.startsWith(`${pageId}:`)));
      return;
    }
    setSelectedPages([...selectedPages, pageId]);
  };

  const toggleFeature = (pageId: string, featureId: string) => {
    const key = `${pageId}:${featureId}`;
    if (selectedFeatures.includes(key)) {
      setSelectedFeatures(selectedFeatures.filter(feature => feature !== key));
      return;
    }
    setSelectedFeatures([...selectedFeatures, key]);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!email.trim()) {
      setError('Vul een e-mailadres in.');
      return;
    }
    if (!currentUserId) {
      setError('Je moet ingelogd zijn om een uitnodiging te sturen.');
      return;
    }
    if (!entityId) {
      setError('Selecteer eerst een entiteit in de entiteit-switcher.');
      return;
    }
    if (restrictToNetworkclub && activeEntity?.type !== 'networkclub') {
      setError('Deze uitnodiging is alleen beschikbaar voor netwerkclubs.');
      return;
    }
    if (selectedPages.length === 0) {
      setError('Selecteer minimaal één pagina.');
      return;
    }
    if (selectedFeatures.length === 0) {
      setError('Selecteer minimaal één onderdeel.');
      return;
    }
    if (!expiresAt) {
      setError('Selecteer een vervaldatum.');
      return;
    }

    const expiresDate = new Date(expiresAt);
    if (Number.isNaN(expiresDate.getTime())) {
      setError('Vervaldatum is ongeldig.');
      return;
    }

    setSubmitting(true);
    try {
      const token = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

      await setDoc(doc(collection(db, 'invitations'), token), {
        entityId,
        invitedEmail: email.trim(),
        role,
        pages: selectedPages,
        features: selectedFeatures,
        status: 'pending',
        createdAt: serverTimestamp(),
        createdBy: currentUserId,
        expiresAt: Timestamp.fromDate(expiresDate)
      });

      setEmail('');
      setSelectedPages([]);
      setSelectedFeatures([]);
      setExpiresAt('');
      setSuccess('Uitnodiging is verstuurd.');
    } catch (err) {
      console.error('Error creating invitation:', err);
      setError(getRulesErrorMessage(err, 'Je hebt geen bevoegdheid om deze uitnodiging te versturen.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Show subscription gate if user cannot invite
  if (!canInvite && !loadingSubscription) {
    return (
      <div className="border border-amber-200 rounded-lg p-6 bg-amber-50">
        <div className="text-center">
          <div className="text-4xl mb-3">🔒</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Upgrade Vereist</h3>
          <p className="text-sm text-gray-700 mb-4">
            {getUpgradeMessage('invite', activeEntity?.type as any)}
          </p>
          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <p>✅ Leden uitnodigen voor je {activeEntity?.type || 'organisatie'}</p>
            <p>✅ Rollen en permissions beheren</p>
            <p>✅ Events organiseren</p>
            <p>✅ Publiekelijk zichtbaar worden</p>
          </div>
          <button
            onClick={() => window.location.hash = '#subscription'}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
          >
            Bekijk Abonnementen
          </button>
        </div>
      </div>
    );
  }

  if (loadingSubscription) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="text-center text-gray-500 py-8">
          <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto"></div>
          <p className="mt-2 text-sm">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">E-mailadres *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="naam@domein.nl"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Actieve entiteit *</label>
          <div className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-700">
            {activeEntity ? `${activeEntity.name} (${activeEntity.type})` : 'Geen actieve entiteit geselecteerd'}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Pagina’s *</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(PAGE_FEATURES).map(([pageId, config]) => (
              <label key={pageId} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedPages.includes(pageId)}
                  onChange={() => togglePage(pageId)}
                />
                {config.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Onderdelen *</p>
          <div className="space-y-2">
            {Object.entries(PAGE_FEATURES).map(([pageId, config]) => (
              <div key={pageId} className="border border-gray-100 rounded-md p-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  {config.label}
                </p>
                <div className="flex flex-wrap gap-3">
                  {config.features.map(feature => (
                    <label key={feature.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={selectedFeatures.includes(`${pageId}:${feature.id}`)}
                        onChange={() => toggleFeature(pageId, feature.id)}
                        disabled={!selectedPages.includes(pageId)}
                      />
                      {feature.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vervalt op *</label>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {error && <p className="text-sm text-[#4F6660]">{error}</p>}
        {success && <p className="text-sm text-green-700">{success}</p>}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Versturen...' : 'Verstuur uitnodiging'}
        </button>
      </div>
    </div>
  );
};

export default InviteRoleForm;
